import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Card from '@mui/material/Card';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import Divider from '@mui/material/Divider';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import InfoIcon from '@mui/icons-material/Info';
import MDButton from 'components/MDButton';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { db, collection, query, getDocs, addDoc, updateDoc, doc } from '../firebaseConfig';
import './DeviationGraph.css';
import { usePeriod } from './PeriodContext';
import BarChartIcon from '@mui/icons-material/BarChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';

const DeviationGraph = ({ userID, trades }) => {
  const [loading, setLoading] = useState(true);
  const [displayModal, setDisplayModal] = useState(false);
  const theme = useTheme();
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades } = usePeriod();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];
  const [chartType, setChartType] = useState('area');

  useEffect(() => {
    const loadChartPreference = async () => {
      try {
        const prefDoc = await getDocs(query(collection(db, 'Users', userID, 'preferences')));
        const prefs = prefDoc.docs[0]?.data();
        if (prefs?.chartType) {
          setChartType(prefs.chartType);
        }
      } catch (error) {
        console.error('Error loading chart preference:', error);
      }
      setLoading(false);
    };
    if (userID) {
      loadChartPreference();
    }
  }, [userID]);

  const saveChartPreference = async (type) => {
    try {
      const prefsRef = collection(db, 'Users', userID, 'preferences');
      const prefQuery = query(prefsRef);
      const prefSnapshot = await getDocs(prefQuery);

      if (prefSnapshot.empty) {
        await addDoc(prefsRef, { chartType: type });
      } else {
        const docRef = doc(db, 'Users', userID, 'preferences', prefSnapshot.docs[0].id);
        await updateDoc(docRef, { chartType: type });
      }
    } catch (error) {
      console.error('Error saving chart preference:', error);
    }
  };

  const processData = () => {
    if (!trades || trades.length === 0) return [];

    const dailyPL = {};
    const filteredTrades = trades.filter((entry) => entry.status === (showMissedTrades ? 'Missed' : 'Closed'));

    filteredTrades.forEach((entry) => {
      const date = new Date(entry.date);
      if (date.toString() === 'Invalid Date' || isNaN(parseFloat(entry.returns))) {
        return;
      }
      const dateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
      if (!dailyPL[dateStr]) {
        dailyPL[dateStr] = 0;
      }
      dailyPL[dateStr] += parseFloat(entry.returns);
    });

    let cumulativePL = 0;
    const processedData = Object.keys(dailyPL)
      .sort()
      .map((date) => {
        cumulativePL += dailyPL[date];
        return { date, netPL: cumulativePL, returns: dailyPL[date] };
      })
      .filter((entry) => matchesPeriod(new Date(entry.date)));
    return processedData;
  };

  const matchesPeriod = (date) => {
    const entryDate = new Date(date);

    if (selectedPeriod === 'today') {
      return entryDate.toDateString() === new Date().toDateString();
    } else if (selectedPeriod === 'this-week') {
      const getWeek = (d) => {
        const start = new Date(d.getFullYear(), 0, 1);
        return Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7);
      };
      return getWeek(entryDate) === getWeek(new Date()) && entryDate.getFullYear() === new Date().getFullYear();
    } else if (selectedPeriod === 'this-month') {
      return entryDate.getMonth() === new Date().getMonth() && entryDate.getFullYear() === new Date().getFullYear();
    } else if (selectedPeriod === 'this-year') {
      return entryDate.getFullYear() === new Date().getFullYear();
    } else if (selectedPeriod === 'pick-month') {
      return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
    } else if (selectedPeriod === 'pick-year') {
      return entryDate.getFullYear() === selectedYear;
    } else if (selectedPeriod === 'pick-month-year') {
      return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
    } else {
      return true; // all-time
    }
  };

  const data = processData();

  const handleModalClose = () => {
    setDisplayModal(false);
  };

  const handleChartClick = () => {
    setDisplayModal(true);
  };

  const gradientOffset = () => {
    const dataMax = Math.max(...data.map((i) => i.netPL));
    const dataMin = Math.min(...data.map((i) => i.netPL));

    if (dataMax <= 0) return 0;
    if (dataMin >= 0) return 1;

    return dataMax / (dataMax - dataMin);
  };

  const NoDataDisplay = () => (
    <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={3}>
      <InfoOutlinedIcon sx={{ fontSize: 48, color: iconColor, mb: 2 }} />
      <MDTypography variant="h6" color="text" textAlign="center" fontWeight="regular">
        No {showMissedTrades ? 'missed' : 'closed'} trading data available yet
      </MDTypography>
      <MDTypography variant="body2" color="text" textAlign="center" sx={{ mt: 1 }}>
        {showMissedTrades ? 'Mark some trades as missed' : 'Complete some trades'} to see your performance charts
      </MDTypography>
    </MDBox>
  );

  const off = gradientOffset();

  const tooltipContent = `Trading Performance Charts:

Area Chart: Shows cumulative profit/loss over time for ${showMissedTrades ? 'missed' : 'closed'} trades.
Bar Chart: Shows individual daily returns for ${showMissedTrades ? 'missed' : 'closed'} trades.
Click the graph icon to the right to switch views.

Features:
• Click chart icon to switch between views
• Click anywhere on chart to expand
• Green indicates profit, red indicates loss
• Hover over points for exact values`;

  return (
    <Card style={{ minHeight: '850px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <ShowChartIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDBox display="flex" alignItems="center" justifyContent="flex-end">
              <IconButton title={tooltipContent} sx={{ padding: 0, marginRight: 1, bgcolor: theme.palette.background.paper }}>
                <InfoIcon sx={{ fontSize: '1rem', color: iconColor, cursor: 'help' }} />
              </IconButton>
              <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
                {chartType === 'area' ? 'Cumulative Daily P/L' : 'Daily P/L'} {showMissedTrades ? '(Missed)' : ''}
              </MDTypography>
              <IconButton
                onClick={() => {
                  const newType = chartType === 'area' ? 'bar' : 'area';
                  setChartType(newType);
                  saveChartPreference(newType);
                }}
                sx={{ ml: 1 }}
              >
                {chartType === 'area' ? <BarChartIcon sx={{ color: iconColor }} /> : <TimelineIcon sx={{ color: iconColor }} />}
              </IconButton>
            </MDBox>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox pb={2} px={2} display="flex" justifyContent="center" alignItems="center" height="100%">
          {loading ? (
            <div>Loading...</div>
          ) : data.length === 0 ? (
            <NoDataDisplay />
          ) : (
            <ResponsiveContainer width="100%" height={650}>
              {chartType === 'area' ? (
                <AreaChart
                  data={data}
                  onClick={handleChartClick}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  className="deviation-chart"
                >
                  <defs>
                    <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
                      <stop offset={off} stopColor={theme.palette.success.main} stopOpacity={0.6} />
                      <stop offset={off} stopColor={theme.palette.error.main} stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: iconColor }} stroke={iconColor} />
                  <YAxis tickFormatter={(value) => `$${value}`} tick={{ fontSize: 12, fill: iconColor }} stroke={iconColor} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: 'none',
                      borderRadius: '4px',
                      boxShadow: theme.shadows[1],
                      color: theme.palette.text.primary,
                    }}
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Returns']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="netPL"
                    stroke={theme.palette.success.main}
                    fill="url(#splitColor)"
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </AreaChart>
              ) : (
                <BarChart
                  data={data}
                  onClick={handleChartClick}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  className="deviation-chart"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: iconColor }} stroke={iconColor} />
                  <YAxis tickFormatter={(value) => `$${value}`} tick={{ fontSize: 12, fill: iconColor }} stroke={iconColor} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: 'none',
                      borderRadius: '4px',
                      boxShadow: theme.shadows[1],
                      color: theme.palette.text.primary,
                    }}
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Returns']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Bar dataKey="returns" barSize={30} maxBarSize={50}>
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={Number(entry.returns) >= 0 ? `${theme.palette.success.main}CC` : `${theme.palette.error.main}CC`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          )}
        </MDBox>
      </MDBox>

      <Dialog
        open={displayModal}
        onClose={handleModalClose}
        maxWidth="xl"
        PaperProps={{
          style: {
            backgroundColor: theme.palette.background.default,
            borderRadius: '16px',
            padding: '24px',
            minWidth: '80%',
          },
        }}
      >
        <MDBox display="flex" justifyContent="flex-end">
          <MDButton
            onClick={handleModalClose}
            variant="text"
            sx={{
              borderRadius: '20px',
              minWidth: 'auto',
              p: 1,
              color: iconColor,
            }}
          >
            ✕
          </MDButton>
        </MDBox>
        <DialogContent>
          <ResponsiveContainer width="100%" height={700}>
            {chartType === 'area' ? (
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="splitColorModal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={off} stopColor={theme.palette.success.main} stopOpacity={0.8} />
                    <stop offset={off} stopColor={theme.palette.error.main} stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: iconColor }} stroke={iconColor} />
                <YAxis tickFormatter={(value) => `$${value}`} tick={{ fontSize: 12, fill: iconColor }} stroke={iconColor} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: 'none',
                    borderRadius: '4px',
                    boxShadow: theme.shadows[1],
                    color: theme.palette.text.primary,
                  }}
                  formatter={(value) => [`$${value.toFixed(2)}`, 'Returns']}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Area
                  type="monotone"
                  dataKey="netPL"
                  stroke={theme.palette.success.main}
                  fill="url(#splitColorModal)"
                  activeDot={{ r: 8 }}
                />
              </AreaChart>
            ) : (
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: iconColor }} stroke={iconColor} />
                <YAxis tickFormatter={(value) => `$${value}`} tick={{ fontSize: 12, fill: iconColor }} stroke={iconColor} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: 'none',
                    borderRadius: '4px',
                    boxShadow: theme.shadows[1],
                    color: theme.palette.text.primary,
                  }}
                  formatter={(value) => [`$${value.toFixed(2)}`, 'Returns']}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Bar dataKey="returns" barSize={40} maxBarSize={60}>
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={Number(entry.returns) >= 0 ? `${theme.palette.success.main}CC` : `${theme.palette.error.main}CC`}
                    />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

DeviationGraph.propTypes = {
  userID: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
};

export default DeviationGraph;